/**
 * „Kalkulator" — the calculator half of the target sheet (nutrition R19, mockup
 * `web-nutrition.html`). The client's data as four tiles, the trainer's choices and the result.
 * A value the profile lacks is typed here for this calculation only and never saved. Each numeric
 * choice is a ± field that takes any value in its range, typed or stepped; holding a button speeds up
 * after a second.
 */

import { NumberField } from '@base-ui/react/number-field';
import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { parseIsoDate } from '~/shared/lib/calendarDate';
import { dateTimeFormat, numberFormat } from '~/shared/lib/intlCache';
import form from '~/shared/ui/formDialog.module.css';
import inline from '~/shared/ui/inlineTextButton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import hiddenControl from '~/shared/ui/visuallyHiddenControl.module.css';

import type { NutritionCalculatorInputs } from '../lib/useNutrition';
import { oneDecimal, signedWhole, wholeNumber } from '../model/nutrition';
import {
  ACTIVITY_FACTOR_RANGE,
  activityBand,
  CALC_FORMULAS,
  commitChoice,
  FAT_PER_KG_RANGE,
  goalDirection,
  GOAL_PERCENT_RANGE,
  initialActivityFactor,
  inputIssues,
  PROTEIN_PER_KG_RANGE,
  requiredInputs,
  resolveBody,
  tapFormula,
  typeBodyFat,
  TYPED_BOUNDS,
  typedInputs,
  typingChoice,
  type CalcSex,
  type ChoiceRange,
  type CalculatorChoices,
  type CalculatorOutcome,
  type CalculatorProfile,
  type CalculatorTyped,
  type TypedInput,
} from '../model/nutritionCalculator';

import { MacroGlyph } from './NutritionGlyphs';
import calc from './NutritionTargetCalculator.module.css';
import styles from './NutritionTab.module.css';

const KEY = 'web:clientDetail.nutrition.calc';
const FAST_AFTER_MS = 1000;
const FAST_STEP_FACTOR = 4;

interface CalculatorProps {
  readonly inputs: NutritionCalculatorInputs;
  readonly typed: CalculatorTyped;
  readonly onTyped: (next: CalculatorTyped) => void;
  readonly choices: CalculatorChoices;
  readonly onChoices: (next: CalculatorChoices) => void;
  readonly outcome: CalculatorOutcome | null;
  /** A save was pressed with the calculator incomplete: every input still wrong is marked. */
  readonly attempted: boolean;
  /** The inputs the trainer supplies, so a failed save can focus the first one marked. */
  readonly fieldRefs: RefObject<Partial<Record<TypedInput, HTMLElement | null>>>;
}

/** How an input's tile is drawn: as usual, marked wrong, or awaited by a tapped Katch-McArdle. */
type TileState = 'calm' | 'danger' | 'awaiting';

export function NutritionTargetCalculator({
  inputs,
  typed,
  onTyped,
  choices,
  onChoices,
  outcome,
  attempted,
  fieldRefs,
}: CalculatorProps) {
  const { t } = useTranslation();
  if (inputs.refused) return <p className={calc.state}>{t(`${KEY}.trainerOnly`)}</p>;
  if (inputs.failed) {
    return (
      <p className={calc.state} role="alert">
        {t(`${KEY}.failed`)}{' '}
        <button type="button" className={inline.inlineTextButton} onClick={inputs.retry}>
          {t(`${KEY}.retry`)}
        </button>
      </p>
    );
  }
  if (inputs.profile === null) return <p className={calc.state}>{t(`${KEY}.loading`)}</p>;
  return (
    <Calculator
      profile={inputs.profile}
      typed={typed}
      onTyped={onTyped}
      choices={choices}
      onChoices={onChoices}
      outcome={outcome}
      attempted={attempted}
      fieldRefs={fieldRefs}
    />
  );
}

function Calculator({
  profile,
  typed,
  onTyped,
  choices,
  onChoices,
  outcome,
  attempted,
  fieldRefs,
}: Omit<CalculatorProps, 'inputs'> & { readonly profile: CalculatorProfile }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const ids = useId();
  const [awaitingKatch, setAwaitingKatch] = useState(false);
  const [blurred, setBlurred] = useState<ReadonlySet<TypedInput>>(new Set());
  const measured = profile.weight === null ? null : shortDate(profile.weight.measuredAt, locale);
  const intakeFactor = initialActivityFactor(profile);
  const factor = choices.activityFactor ?? intakeFactor;
  let activityHint: string | undefined;
  if (profile.activity === null) {
    activityHint = t(`${KEY}.activityNone`);
  } else if (Math.abs(factor - intakeFactor) < 1e-9) {
    activityHint = t(`${KEY}.activityFrom`, { level: t(`${KEY}.activityAdjective.${profile.activity}`) });
  }
  const direction = goalDirection(choices.goalPercent);
  const result = outcome?.kind === 'ready' ? outcome.result : null;
  const profileBodyFat = profile.weight?.bodyFatPercent ?? null;
  const bodyFat = resolveBody(profile, typed).bodyFatPercent;
  const typing = typedInputs(profile);
  const katchNeeds = t(`${KEY}.katchNeedsBodyFat`);
  const issues = inputIssues(profile, typed, choices.formula);
  const required = requiredInputs(profile, typed, choices.formula);
  const awaitingBodyFat = awaitingKatch && bodyFat === null;
  const field = (input: TypedInput, source: string, unit = ''): TypedFieldProps => {
    const issue = issues[input];
    const danger = issue !== undefined && (attempted || (issue === 'invalid' && blurred.has(input)));
    let note = source;
    if (danger && issue === 'invalid' && input !== 'sex') {
      const [min, max] = TYPED_BOUNDS[input];
      note = t(`${KEY}.typedRange`, { min: wholeNumber(min, locale), max: wholeNumber(max, locale), unit });
    } else if (input === 'bodyFat') {
      if (awaitingBodyFat) note = t(`${KEY}.neededForKatch`);
      else if (typed.bodyFat.trim() === '' || issue !== undefined) note = t(`${KEY}.optionalNote`, { source });
    } else if (required.has(input)) {
      note = t(`${KEY}.requiredNote`, { source });
    }
    let state: TileState = 'calm';
    if (danger) state = 'danger';
    else if (input === 'bodyFat' && awaitingBodyFat) state = 'awaiting';
    return {
      note,
      state,
      inputRef: (node) => {
        fieldRefs.current[input] = node;
      },
      onBlur: (text) => {
        if (text.trim() !== '' && !blurred.has(input)) setBlurred(new Set(blurred).add(input));
      },
    };
  };

  return (
    <>
      <div className={calc.sources} role="group" aria-label={t(`${KEY}.sources`)}>
        <SexTile
          profile={profile}
          typed={typed}
          onTyped={onTyped}
          {...field('sex', profile.sex === 'other' ? t(`${KEY}.sexOther`) : t(`${KEY}.missingProfile`))}
        />
        {profile.ageYears === null ? (
          <TypedTile
            label={t(`${KEY}.age`)}
            unit={t(`${KEY}.years`)}
            {...field('age', t(`${KEY}.missingProfile`), t(`${KEY}.years`))}
            value={typed.age}
            onChange={(age) => {
              onTyped({ ...typed, age });
            }}
          />
        ) : (
          <Tile
            label={t(`${KEY}.age`)}
            value={String(profile.ageYears)}
            unit={t(`${KEY}.years`)}
            note={
              profile.dateOfBirth === null
                ? t(`${KEY}.fromProfile`)
                : t(`${KEY}.born`, { date: fullDate(profile.dateOfBirth, locale) })
            }
          />
        )}
        {profile.heightCm === null ? (
          <TypedTile
            label={t(`${KEY}.height`)}
            unit="cm"
            {...field('height', t(`${KEY}.missingProfile`), 'cm')}
            value={typed.height}
            onChange={(height) => {
              onTyped({ ...typed, height });
            }}
          />
        ) : (
          <Tile
            label={t(`${KEY}.height`)}
            value={wholeNumber(profile.heightCm, locale)}
            unit="cm"
            note={t(`${KEY}.fromProfile`)}
          />
        )}
        {profile.weight === null ? (
          <TypedTile
            label={t(`${KEY}.weight`)}
            unit="kg"
            {...field('weight', t(`${KEY}.missingWeight`), 'kg')}
            value={typed.weight}
            onChange={(weight) => {
              onTyped({ ...typed, weight });
            }}
          />
        ) : (
          <Tile
            label={t(`${KEY}.weight`)}
            value={oneDecimal(profile.weight.kg, locale)}
            unit="kg"
            note={
              <>
                {t(`${KEY}.measured`, { date: measured })}
                {profile.weight.isStale ? (
                  <>
                    {' · '}
                    <span className={calc.stale} title={t(`${KEY}.staleLabel`)}>
                      <span aria-hidden="true">{t(`${KEY}.stale`)}</span>
                      <span className={hidden.srOnly}>{t(`${KEY}.staleLabel`)}</span>
                    </span>
                  </>
                ) : null}
              </>
            }
          />
        )}
        {profileBodyFat === null ? (
          <TypedTile
            label={t(`${KEY}.bodyFat`)}
            unit="%"
            {...field(
              'bodyFat',
              profile.weight === null ? t(`${KEY}.missingWeight`) : t(`${KEY}.missingBodyFat`),
              '%',
            )}
            value={typed.bodyFat}
            onChange={(next) => {
              onTyped({ ...typed, bodyFat: next });
              const settled = typeBodyFat(choices, awaitingKatch, next);
              if (settled.choices !== choices) onChoices(settled.choices);
              setAwaitingKatch(settled.awaitingBodyFat);
            }}
          />
        ) : null}
      </div>
      {typing.size > 0 ? (
        <p className={`${String(form.note)} ${String(styles.targetHint)}`}>{t(`${KEY}.typedNote`)}</p>
      ) : null}

      <Stepper
        label={t(`${KEY}.activity`)}
        hint={activityHint}
        value={factor}
        range={ACTIVITY_FACTOR_RANGE}
        digits={{ min: 0, max: 3 }}
        prefix="×"
        word={t(`${KEY}.activityLevel.${activityBand(factor)}`)}
        onChange={(next) => {
          onChoices({ ...choices, activityFactor: next });
        }}
      />
      <Stepper
        label={t(`${KEY}.goal`)}
        value={choices.goalPercent}
        range={GOAL_PERCENT_RANGE}
        digits={{ min: 0, max: 0 }}
        unit="%"
        word={t(`${KEY}.goalDirection.${direction}`)}
        readout={
          result === null || direction === 'maintenance'
            ? null
            : t(`${KEY}.goalEffect`, {
                percent: `${signedWhole(result.goalPercent, locale)}%`,
                kcal: signedWhole(result.goalKcal, locale),
              })
        }
        onChange={(next) => {
          onChoices({ ...choices, goalPercent: next });
        }}
      />
      <Choice
        label={t(`${KEY}.formula`)}
        name={`${ids}-formula`}
        options={CALC_FORMULAS.map((formula) => ({
          value: formula,
          label: t(`${KEY}.formulas.${formula}`),
          unavailable: formula === 'katch' && bodyFat === null ? katchNeeds : undefined,
        }))}
        value={choices.formula === 'katch' && bodyFat === null ? 'mifflin' : choices.formula}
        message={awaitingBodyFat ? katchNeeds : undefined}
        onChange={(formula) => {
          const pick = tapFormula(choices, formula, bodyFat);
          if (pick.choices !== choices) onChoices(pick.choices);
          setAwaitingKatch(pick.awaitingBodyFat);
          if (pick.awaitingBodyFat) fieldRefs.current.bodyFat?.focus();
        }}
      />
      <Stepper
        label={t(`${KEY}.protein`)}
        value={choices.proteinPerKg}
        range={PROTEIN_PER_KG_RANGE}
        digits={{ min: 1, max: 1 }}
        unit={t(`${KEY}.perKgUnit`)}
        onChange={(next) => {
          onChoices({ ...choices, proteinPerKg: next });
        }}
      />
      <Stepper
        label={t(`${KEY}.fat`)}
        value={choices.fatPerKg}
        range={FAT_PER_KG_RANGE}
        digits={{ min: 1, max: 1 }}
        unit={t(`${KEY}.perKgUnit`)}
        onChange={(next) => {
          onChoices({ ...choices, fatPerKg: next });
        }}
      />

      <Result outcome={outcome} formula={choices.formula} hasBodyFat={bodyFat !== null} />
    </>
  );
}

/** True from a second into a press until it is released anywhere. */
function useFastHold(): readonly [boolean, () => void] {
  const [fast, setFast] = useState(false);
  const release = useRef<(() => void) | null>(null);
  useEffect(
    () => () => {
      release.current?.();
    },
    [],
  );
  const press = () => {
    release.current?.();
    const timer = window.setTimeout(() => {
      setFast(true);
    }, FAST_AFTER_MS);
    const stop = () => {
      window.clearTimeout(timer);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      release.current = null;
      setFast(false);
    };
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    release.current = stop;
  };
  return [fast, press];
}

/** What the trainer is typing into a ± field, and the value it held before. */
interface StepperDraft {
  readonly text: string;
  readonly before: number;
}

const TYPABLE = /^[\d\s.,+\-\u2212\u2013]*$/;

/**
 * A choice as a ± field with the word that names it. A typed value counts while typing once it is in
 * range and commits on blur, Enter or a ± press, clamped and rounded but never snapped to the step.
 */
function Stepper({
  label,
  hint,
  value,
  range,
  digits,
  prefix,
  unit,
  word,
  readout,
  onChange,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly value: number;
  readonly range: ChoiceRange;
  readonly digits: { readonly min: number; readonly max: number };
  readonly prefix?: string;
  readonly unit?: string;
  readonly word?: string;
  readonly readout?: ReactNode;
  readonly onChange: (value: number) => void;
}) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const [fast, press] = useFastHold();
  const [draft, setDraft] = useState<StepperDraft | null>(null);
  const fastStep = range.step * FAST_STEP_FACTOR;
  const format = { minimumFractionDigits: digits.min, maximumFractionDigits: digits.max };
  const shown = numberFormat(i18n.language, format).format(value).replace('-', '\u2212');
  const chars =
    String(Math.trunc(Math.max(Math.abs(range.min), Math.abs(range.max)))).length +
    (range.min < 0 ? 1 : 0) +
    (digits.max > 0 ? digits.max + 1 : 0);

  const commit = () => {
    if (draft === null) return;
    const next = commitChoice(draft.text, range) ?? draft.before;
    setDraft(null);
    if (next !== value) onChange(next);
  };
  // Base UI steps from the rendered value, so the typed one has to land before the step.
  const commitBeforeStep = () => {
    if (draft !== null) flushSync(commit);
  };

  return (
    <div className={form.field}>
      <FieldHead label={label} labelId={`${id}-label`} hint={hint} hintId={`${id}-hint`} />
      <div className={calc.precise}>
        <NumberField.Root
          value={value}
          min={range.min}
          max={range.max}
          step={fast ? fastStep : range.step}
          largeStep={fastStep}
          allowWheelScrub={false}
          locale={i18n.language}
          format={format}
          onValueChange={(next, details) => {
            if (next !== null && details.reason !== 'input-blur') onChange(next);
          }}
        >
          <NumberField.Group className={calc.stepper}>
            <NumberField.Decrement
              className={calc.stepperButton}
              aria-label={t(`${KEY}.stepLess`, { label })}
              onPointerDown={() => {
                commitBeforeStep();
                press();
              }}
              onClick={commitBeforeStep}
            >
              −
            </NumberField.Decrement>
            {prefix === undefined ? null : (
              <span className={calc.stepperAffix} aria-hidden="true">
                {prefix}
              </span>
            )}
            <NumberField.Input
              className={calc.stepperValue}
              style={{ '--stepper-chars': String(chars) } as CSSProperties}
              aria-label={label}
              aria-describedby={hint === undefined ? undefined : `${id}-hint`}
              inputMode={range.min < 0 ? 'text' : 'decimal'}
              value={draft?.text ?? shown}
              onChange={(event) => {
                event.preventBaseUIHandler();
                const text = event.currentTarget.value;
                if (!TYPABLE.test(text)) return;
                setDraft({ text, before: draft?.before ?? value });
                const typing = typingChoice(text, range);
                if (typing !== null && typing !== value) onChange(typing);
              }}
              onPaste={(event) => {
                event.preventBaseUIHandler();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && draft !== null) {
                  event.preventDefault();
                  commit();
                } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                  commitBeforeStep();
                } else if (event.key.length === 1 || draft !== null) {
                  event.preventBaseUIHandler();
                }
              }}
              onBlur={commit}
            />
            {unit === undefined ? null : (
              <span className={calc.stepperAffix} aria-hidden="true">
                {unit}
              </span>
            )}
            <NumberField.Increment
              className={calc.stepperButton}
              aria-label={t(`${KEY}.stepMore`, { label })}
              onPointerDown={() => {
                commitBeforeStep();
                press();
              }}
              onClick={commitBeforeStep}
            >
              +
            </NumberField.Increment>
          </NumberField.Group>
        </NumberField.Root>
        <span className={calc.preciseReadout} aria-live="polite">
          {word === undefined ? null : <span className={calc.preciseWord}>{word}</span>}
          {word !== undefined && readout != null ? ' · ' : null}
          {readout}
        </span>
      </div>
    </div>
  );
}

function FieldHead({
  label,
  labelId,
  hint,
  hintId,
}: {
  readonly label: string;
  readonly labelId: string;
  readonly hint?: string;
  readonly hintId: string;
}) {
  return (
    <div className={calc.fieldHead}>
      <span id={labelId}>{label}</span>
      {hint === undefined ? null : (
        <small className={calc.fieldHint} id={hintId}>
          {hint}
        </small>
      )}
    </div>
  );
}

interface TypedFieldProps {
  readonly note: string;
  readonly state: TileState;
  readonly inputRef: (node: HTMLElement | null) => void;
  readonly onBlur: (text: string) => void;
}

const TILE_STATE: Readonly<Record<TileState, string>> = {
  calm: '',
  danger: String(calc.sourceDanger),
  awaiting: String(calc.sourceAwaiting),
};

function SexTile({
  profile,
  typed,
  onTyped,
  note,
  state,
  inputRef,
}: TypedFieldProps & {
  readonly profile: CalculatorProfile;
  readonly typed: CalculatorTyped;
  readonly onTyped: (next: CalculatorTyped) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  if (profile.sex === 'female' || profile.sex === 'male') {
    return <Tile label={t(`${KEY}.sex`)} value={t(`${KEY}.sexValue.${profile.sex}`)} note={t(`${KEY}.fromProfile`)} />;
  }
  return (
    <div className={`${String(calc.source)} ${String(calc.sourceMissing)} ${TILE_STATE[state]}`}>
      <label className={calc.sourceLabel} htmlFor={id}>
        {t(`${KEY}.sex`)}
      </label>
      <select
        ref={inputRef}
        className={calc.sourceInput}
        id={id}
        aria-invalid={state === 'danger' ? true : undefined}
        aria-describedby={`${id}-note`}
        value={typed.sex ?? ''}
        onChange={(event) => {
          const value = event.target.value;
          onTyped({ ...typed, sex: value === '' ? null : (value as CalcSex) });
        }}
      >
        <option value="">{t(`${KEY}.sexChoose`)}</option>
        <option value="female">{t(`${KEY}.sexValue.female`)}</option>
        <option value="male">{t(`${KEY}.sexValue.male`)}</option>
      </select>
      <span className={calc.sourceNote} id={`${id}-note`}>
        {note}
      </span>
    </div>
  );
}

function Tile({
  label,
  value,
  unit,
  note,
}: {
  readonly label: string;
  readonly value: string;
  readonly unit?: string;
  readonly note: React.ReactNode;
}) {
  return (
    <div className={calc.source}>
      <span className={calc.sourceLabel}>{label}</span>
      <span className={calc.sourceValue}>
        {value}
        {unit === undefined ? null : <em className={calc.sourceUnit}>{unit}</em>}
      </span>
      <span className={calc.sourceNote}>{note}</span>
    </div>
  );
}

function TypedTile({
  label,
  unit,
  note,
  state,
  value,
  inputRef,
  onBlur,
  onChange,
}: TypedFieldProps & {
  readonly label: string;
  readonly unit: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
}) {
  const id = useId();
  return (
    <div className={`${String(calc.source)} ${String(calc.sourceMissing)} ${TILE_STATE[state]}`}>
      <label className={calc.sourceLabel} htmlFor={id}>
        {label}
      </label>
      <span className={calc.sourceTyped}>
        <input
          ref={inputRef}
          className={calc.sourceInput}
          id={id}
          inputMode="decimal"
          aria-invalid={state === 'danger' ? true : undefined}
          aria-describedby={`${id}-note`}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          onBlur={(event) => {
            onBlur(event.target.value);
          }}
        />
        <em className={calc.sourceUnit}>{unit}</em>
      </span>
      <span className={calc.sourceNote} id={`${id}-note`}>
        {note}
      </span>
    </div>
  );
}

interface ChoiceOption<Value extends string> {
  readonly value: Value;
  readonly label: string;
  /** Why the option cannot be picked yet; it stays focusable and pressable. */
  readonly unavailable?: string;
}

function Choice<Value extends string>({
  label,
  name,
  options,
  value,
  message,
  onChange,
}: {
  readonly label: string;
  readonly name: string;
  readonly options: readonly ChoiceOption<Value>[];
  readonly value: Value | null;
  readonly message?: string;
  readonly onChange: (value: Value) => void;
}) {
  const labelId = `${name}-label`;
  return (
    <div className={form.field}>
      <FieldHead label={label} labelId={labelId} hintId={`${name}-hint`} />
      <div className={calc.options} role="radiogroup" aria-labelledby={labelId}>
        {options.map((option) => {
          const on = option.value === value;
          return (
            <label
              key={option.value}
              className={`${String(calc.option)} ${on ? String(calc.optionOn) : ''} ${
                option.unavailable === undefined ? '' : String(calc.optionOff)
              }`}
            >
              <input
                className={hiddenControl.control}
                type="radio"
                name={name}
                value={option.value}
                checked={on}
                aria-disabled={option.unavailable === undefined ? undefined : true}
                aria-describedby={option.unavailable === undefined ? undefined : `${name}-${option.value}-reason`}
                onChange={() => {
                  onChange(option.value);
                }}
                onClick={() => {
                  if (on) onChange(option.value);
                }}
              />
              {option.label}
            </label>
          );
        })}
      </div>
      {options.map((option) =>
        option.unavailable === undefined ? null : (
          <span key={option.value} className={hidden.srOnly} id={`${name}-${option.value}-reason`}>
            {option.unavailable}
          </span>
        ),
      )}
      <p className={calc.choiceMessage} aria-live="polite">
        {message}
      </p>
    </div>
  );
}

function Result({
  outcome,
  formula: chosen,
  hasBodyFat,
}: {
  readonly outcome: CalculatorOutcome | null;
  readonly formula: CalculatorChoices['formula'];
  readonly hasBodyFat: boolean;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const decimal = (value: number) => numberFormat(locale, { maximumFractionDigits: 3 }).format(value);
  const result = outcome?.kind === 'ready' ? outcome.result : null;
  const dash = '—';
  const formula = chosen === 'katch' && !hasBodyFat ? 'mifflin' : chosen;

  let why: string;
  if (result !== null) {
    why = t(`${KEY}.why`, {
      formula: t(`${KEY}.formulas.${formula}`),
      resting: wholeNumber(result.resting, locale),
      factor: decimal(result.factor),
      activity: t(`${KEY}.activityLevel.${activityBand(result.factor)}`),
      goal:
        result.goalPercent === 0 ? '' : t(`${KEY}.whyGoal`, { mark: `${signedWhole(result.goalPercent, locale)}%` }),
      protein: oneDecimal(result.proteinPerKg, locale),
      fat: oneDecimal(result.fatPerKg, locale),
    });
  } else {
    why = t(`${KEY}.fillRequired`);
  }

  return (
    <div className={calc.result}>
      <div>
        <small className={calc.resultLabel}>{t(`${KEY}.resting`)}</small>
        <b className={calc.resultValue}>{result === null ? dash : wholeNumber(result.resting, locale)}</b>
      </div>
      <div>
        <small className={calc.resultLabel}>{t(`${KEY}.withActivity`)}</small>
        <b className={calc.resultValue}>{result === null ? dash : wholeNumber(result.withActivity, locale)}</b>
      </div>
      <div>
        <small className={calc.resultLabel}>{t(`${KEY}.daily`)}</small>
        <b className={`${String(calc.resultValue)} ${String(calc.resultBig)}`}>
          {result === null ? dash : wholeNumber(result.energy, locale)}
          <span className={calc.resultUnit}>{'\u00a0'}kcal</span>
        </b>
      </div>
      <div className={calc.resultMacros}>
        {(['protein', 'carbs', 'fat'] as const).map((nutrient) => (
          <span className={calc.resultMacro} key={nutrient}>
            <MacroGlyph nutrient={nutrient} small />
            <span className={hidden.srOnly}>{t(`web:clientDetail.nutrition.nutrient.${nutrient}`)}</span>
            {result === null ? dash : `${wholeNumber(result[nutrient], locale)}\u00a0g`}
          </span>
        ))}
      </div>
      <p className={calc.resultWhy}>{why}</p>
      {result?.carbsShort === true ? <p className={calc.resultShort}>{t(`${KEY}.carbsShort`)}</p> : null}
    </div>
  );
}

/** A civil date as „14.03.1997". */
function fullDate(date: string, locale: string): string {
  const parsed = parseIsoDate(date);
  return parsed === null
    ? date
    : dateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(parsed);
}

/** An instant as „9 paź" in the viewer's zone. */
function shortDate(instant: string, locale: string): string {
  const parsed = new Date(instant);
  return Number.isNaN(parsed.getTime())
    ? instant
    : dateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(parsed);
}
